import React from 'react'
import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  InputAdornment,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  CircularProgress,
  Stack,
  Divider,
  Fade,
  Badge,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import {
  AddCircleOutlineOutlined,
  Refresh,
  Android,
  Apple,
  CheckCircle,
  WarningAmber,
  Edit,
  Delete,
  Search,
  FilterList,
  Clear,
  ViewList,
  ViewModule,
  Apps,
  School,
  BadgeOutlined,
  SelectAll,
  Deselect,
  SystemUpdateAlt,
  DeleteSweep,
  EditOutlined,
  Check,
  Close,
} from '@mui/icons-material'
import Swal from 'sweetalert2'
import { useNavigate } from 'react-router-dom'
import { getRequest, postRequest, putRequest, deleteRequest } from 'Includes/api/apicall'
import { GET_URL, POST_URL, PUT_URL, DEL_URL } from 'Includes/urls'

const APP_TYPES = [
  { value: 'student', label: 'Student App' },
  { value: 'staff', label: 'Staff App' },
]

const DEVICE_TYPES = [
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
]

// API helpers never throw: on failure they show an error popup and resolve with undefined.
const isOk = (res) => Boolean(res) && (res.status === 200 || res.status === 201)

const initialForm = {
  app_host: '',
  version_name: '',
  app_type: 'student',
  device_type: 'android',
  is_current_version: true,
  need_force_update: false,
}

function AppVersionList(props) {
  const navigate = useNavigate()

  // State for data
  const [rows, setRows] = React.useState([])
  const [totalCount, setTotalCount] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [existingPackages, setExistingPackages] = React.useState([])

  // Search & Filter state
  const [searchTerm, setSearchTerm] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [selectedPackage, setSelectedPackage] = React.useState('')
  const [filterAppType, setFilterAppType] = React.useState('all')
  const [filterDeviceType, setFilterDeviceType] = React.useState('all')
  const [filterCurrentOnly, setFilterCurrentOnly] = React.useState(false)
  const [viewMode, setViewMode] = React.useState('table') // 'table' | 'cards'

  // Pagination & Sorting
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(25)

  // Modal Dialog State
  const [modalOpen, setModalOpen] = React.useState(false)
  const [modalMode, setModalMode] = React.useState('create_version') // 'new_package' | 'create_version' | 'edit_version'
  const [form, setForm] = React.useState(initialForm)
  const [editingId, setEditingId] = React.useState(null)
  const [formErrors, setFormErrors] = React.useState({})
  const [submitting, setSubmitting] = React.useState(false)
  const [createBothPlatforms, setCreateBothPlatforms] = React.useState(false)

  // Multi-select state
  const [selectedIds, setSelectedIds] = React.useState([])
  const [bulkDialogOpen, setBulkDialogOpen] = React.useState(false)
  const [bulkAction, setBulkAction] = React.useState('') // 'update_version' | 'set_current' | 'force_update_on' | 'force_update_off' | 'delete'
  const [bulkVersionName, setBulkVersionName] = React.useState('')
  const [bulkSubmitting, setBulkSubmitting] = React.useState(false)

  // Inline edit state
  const [inlineEditId, setInlineEditId] = React.useState(null)
  const [inlineEditValue, setInlineEditValue] = React.useState('')
  const [inlineSaving, setInlineSaving] = React.useState(false)

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(0)
    }, 350)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch unique package names
  const fetchPackages = React.useCallback(() => {
    getRequest(`${GET_URL.app_version.api}packages/`, {}, props)
      .then((res) => {
        if (res && res.status === 200 && Array.isArray(res.data?.data)) {
          setExistingPackages(res.data.data.filter(Boolean))
        }
      })
      .catch((err) => {
        console.error('Failed to load packages:', err)
      })
  }, [props])

  // Fetch version rows
  const loadRows = React.useCallback(() => {
    setLoading(true)
    const params = {
      pageno: page + 1,
      limit: pageSize,
      page: page + 1,
      page_size: pageSize,
    }

    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim()
    }
    if (selectedPackage) {
      params.app_host = selectedPackage
    }
    if (filterAppType && filterAppType !== 'all') {
      params.app_type = filterAppType
    }
    if (filterDeviceType && filterDeviceType !== 'all') {
      params.device_type = filterDeviceType
    }
    if (filterCurrentOnly) {
      params.is_current_version = 'true'
    }

    getRequest(GET_URL.app_version.api, params, props)
      .then((res) => {
        if (res && res.status === 200) {
          const list = res.data?.data || []
          const count = res.data?.total_count ?? res.data?.count ?? (Array.isArray(list) ? list.length : 0)
          setRows(Array.isArray(list) ? list : [])
          setTotalCount(count)
        }
      })
      .catch((err) => {
        console.error('Failed to load app versions:', err)
        Swal.fire({
          icon: 'error',
          title: 'Error loading app versions',
          text: err?.message || 'Please try again',
        })
      })
      .finally(() => setLoading(false))
  }, [page, pageSize, debouncedSearch, selectedPackage, filterAppType, filterDeviceType, filterCurrentOnly, props])

  React.useEffect(() => {
    fetchPackages()
  }, [fetchPackages])

  React.useEffect(() => {
    loadRows()
  }, [loadRows])

  // Computed metrics
  const metrics = React.useMemo(() => {
    const uniquePkgs = new Set(rows.map((r) => r.app_host).filter(Boolean))
    const currentActive = rows.filter((r) => r.is_current_version).length
    const forceUpdates = rows.filter((r) => r.need_force_update).length
    const androidCurrent = rows.filter((r) => r.device_type === 'android' && r.is_current_version).length
    const iosCurrent = rows.filter((r) => r.device_type === 'ios' && r.is_current_version).length

    return {
      totalPackages: uniquePkgs.size || existingPackages.length,
      totalReleases: totalCount,
      currentActive,
      androidCurrent,
      iosCurrent,
      forceUpdates,
    }
  }, [rows, totalCount, existingPackages])

  // Open modal for Adding a New Package
  const handleOpenAddPackage = () => {
    setModalMode('new_package')
    setEditingId(null)
    setForm({
      app_host: '',
      version_name: '1.0.0',
      app_type: 'student',
      device_type: 'android',
      is_current_version: true,
      need_force_update: false,
    })
    setCreateBothPlatforms(true)
    setFormErrors({})
    setModalOpen(true)
  }

  // Open modal for Adding a New Version to existing package
  const handleOpenAddVersion = (presetPackage = '') => {
    setModalMode('create_version')
    setEditingId(null)
    setForm({
      app_host: presetPackage || (existingPackages.length > 0 ? existingPackages[0] : ''),
      version_name: '',
      app_type: 'student',
      device_type: 'android',
      is_current_version: true,
      need_force_update: false,
    })
    setCreateBothPlatforms(false)
    setFormErrors({})
    setModalOpen(true)
  }

  // Open modal for Editing an existing record
  const handleOpenEdit = (row) => {
    setModalMode('edit_version')
    setEditingId(row.id)
    setForm({
      app_host: row.app_host || '',
      version_name: row.version_name || '',
      app_type: row.app_type || 'student',
      device_type: row.device_type || 'android',
      is_current_version: Boolean(row.is_current_version),
      need_force_update: Boolean(row.need_force_update),
    })
    setCreateBothPlatforms(false)
    setFormErrors({})
    setModalOpen(true)
  }

  // Validation
  const validateForm = () => {
    const errs = {}
    if (!form.app_host?.trim()) {
      errs.app_host = 'Package name / App Host is required'
    } else if (!/^[a-zA-Z0-9._-]+$/.test(form.app_host.trim())) {
      errs.app_host = 'Package name can only contain letters, numbers, dots, and hyphens'
    }

    if (!form.version_name?.trim()) {
      errs.version_name = 'Version number is required'
    } else if (!/^\d+(\.\d+)*$/.test(form.version_name.trim())) {
      errs.version_name = 'Version should follow semantic numbers (e.g. 1.0.0, 1.2.4)'
    }

    setFormErrors(errs)
    return Object.keys(errs).length === 0
  }

  // Submit Modal
  const handleFormSubmit = async () => {
    if (!validateForm()) return
    setSubmitting(true)

    try {
      if (modalMode === 'edit_version') {
        const payload = {
          app_host: form.app_host.trim(),
          version_name: form.version_name.trim(),
          app_type: form.app_type,
          device_type: form.device_type,
          is_current_version: form.is_current_version,
          need_force_update: form.need_force_update,
        }
        const res = await putRequest(`${PUT_URL.app_version.api}${editingId}/`, payload, props)
        if (!isOk(res)) return
        Swal.fire({
          icon: 'success',
          title: 'Version Updated',
          text: `Updated version ${form.version_name} for ${form.app_host}`,
          timer: 1500,
          showConfirmButton: false,
        })
      } else {
        // Create 1 or 2 records (if both android and ios selected)
        const platforms = createBothPlatforms ? ['android', 'ios'] : [form.device_type]

        const created = []
        for (const platform of platforms) {
          const payload = {
            app_host: form.app_host.trim(),
            version_name: form.version_name.trim(),
            app_type: form.app_type,
            device_type: platform,
            is_current_version: form.is_current_version,
            need_force_update: form.need_force_update,
          }
          const res = await postRequest(POST_URL.app_version.api, payload, props)
          if (!isOk(res)) {
            if (created.length) {
              fetchPackages()
              loadRows()
              Swal.fire({
                icon: 'warning',
                title: 'Partially saved',
                text: `Created for ${created.join(', ')}, but failed for ${platform}.`,
              })
            }
            return
          }
          created.push(platform)
        }

        Swal.fire({
          icon: 'success',
          title: modalMode === 'new_package' ? 'Package Added' : 'Version Released',
          text: `Successfully added ${form.version_name} for ${form.app_host}`,
          timer: 1500,
          showConfirmButton: false,
        })
      }

      setModalOpen(false)
      fetchPackages()
      loadRows()
    } catch (err) {
      console.error('Error saving app version:', err)
      Swal.fire({
        icon: 'error',
        title: 'Error saving version',
        text: err?.response?.data?.Reason || err?.message || 'An error occurred',
      })
    } finally {
      setSubmitting(false)
    }
  }

  // Quick Action: Set as Current Version
  const handleSetCurrent = async (row) => {
    const confirm = await Swal.fire({
      title: 'Set as Current Active Version?',
      html: `Do you want to make <b>v${row.version_name}</b> the current active version for <b>${row.app_host}</b> (${row.app_type} on ${row.device_type})?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Make Current',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10B981',
    })

    if (!confirm.isConfirmed) return

    try {
      const res = await postRequest(`${POST_URL.app_version.api}${row.id}/set-current/`, {}, props)
      if (!isOk(res)) return
      Swal.fire({
        icon: 'success',
        title: 'Updated Active Version',
        text: `v${row.version_name} is now the current version!`,
        timer: 1500,
        showConfirmButton: false,
      })
      loadRows()
    } catch (err) {
      console.error('Failed to set current:', err)
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update current version.' })
    }
  }

  // Quick Action: Toggle Force Update
  const handleToggleForceUpdate = async (row) => {
    try {
      const res = await postRequest(`${POST_URL.app_version.api}${row.id}/toggle-force-update/`, {}, props)
      if (!isOk(res)) return
      loadRows()
    } catch (err) {
      console.error('Failed to toggle force update:', err)
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to toggle force update.' })
    }
  }

  // Delete Action
  const handleDelete = async (row) => {
    const confirm = await Swal.fire({
      title: 'Delete this app version?',
      html: `Are you sure you want to delete <b>v${row.version_name}</b> for package <b>${row.app_host}</b>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      confirmButtonColor: '#EF4444',
      cancelButtonText: 'Cancel',
    })

    if (!confirm.isConfirmed) return

    try {
      const res = await deleteRequest(`${DEL_URL.app_version.api}${row.id}/`, {}, props)
      if (!isOk(res)) return
      Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: `App version v${row.version_name} removed.`,
        timer: 1400,
        showConfirmButton: false,
      })
      loadRows()
      fetchPackages()
    } catch (err) {
      console.error('Failed to delete app version:', err)
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: 'Could not delete the record.' })
    }
  }

  // Reset Filters
  const handleClearFilters = () => {
    setSearchTerm('')
    setSelectedPackage('')
    setFilterAppType('all')
    setFilterDeviceType('all')
    setFilterCurrentOnly(false)
    setPage(0)
  }

  // Bulk Action Handlers
  const handleBulkAction = (action) => {
    if (selectedIds.length === 0) {
      Swal.fire({ icon: 'info', title: 'No items selected', text: 'Please select one or more app versions first.' })
      return
    }
    setBulkAction(action)
    if (action === 'update_version') {
      setBulkVersionName('')
      setBulkDialogOpen(true)
    } else if (action === 'set_current') {
      Swal.fire({
        title: `Set ${selectedIds.length} item(s) as Current?`,
        text: 'This will mark all selected versions as the current active version for their respective app host / type / platform.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Set Current',
        confirmButtonColor: '#10B981',
      }).then(async (result) => {
        if (!result.isConfirmed) return
        try {
          setBulkSubmitting(true)
          const res = await postRequest(`${POST_URL.app_version.api}bulk-update/`, { ids: selectedIds, is_current_version: true }, props)
          if (!isOk(res)) return
          Swal.fire({ icon: 'success', title: 'Updated', text: `${selectedIds.length} version(s) set as current.`, timer: 1500, showConfirmButton: false })
          setSelectedIds([])
          loadRows()
        } catch (err) {
          Swal.fire({ icon: 'error', title: 'Bulk Update Failed', text: err?.response?.data?.Reason || 'An error occurred.' })
        } finally {
          setBulkSubmitting(false)
        }
      })
    } else if (action === 'force_update_on' || action === 'force_update_off') {
      const turnOn = action === 'force_update_on'
      Swal.fire({
        title: `${turnOn ? 'Enable' : 'Disable'} Force Update for ${selectedIds.length} item(s)?`,
        text: turnOn ? 'Users on older versions will be blocked until they update.' : 'Force update will be turned off for selected versions.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: turnOn ? 'Enable Force Update' : 'Disable Force Update',
        confirmButtonColor: turnOn ? '#EF4444' : '#10B981',
      }).then(async (result) => {
        if (!result.isConfirmed) return
        try {
          setBulkSubmitting(true)
          const res = await postRequest(`${POST_URL.app_version.api}bulk-update/`, { ids: selectedIds, need_force_update: turnOn }, props)
          if (!isOk(res)) return
          Swal.fire({ icon: 'success', title: 'Updated', text: `Force update ${turnOn ? 'enabled' : 'disabled'} for ${selectedIds.length} version(s).`, timer: 1500, showConfirmButton: false })
          setSelectedIds([])
          loadRows()
        } catch (err) {
          Swal.fire({ icon: 'error', title: 'Bulk Update Failed', text: err?.response?.data?.Reason || 'An error occurred.' })
        } finally {
          setBulkSubmitting(false)
        }
      })
    } else if (action === 'delete') {
      Swal.fire({
        title: `Delete ${selectedIds.length} selected item(s)?`,
        text: 'This action cannot be undone. All selected app version records will be permanently removed.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Delete All',
        confirmButtonColor: '#EF4444',
      }).then(async (result) => {
        if (!result.isConfirmed) return
        try {
          setBulkSubmitting(true)
          const failedIds = []
          for (const id of selectedIds) {
            const res = await deleteRequest(`${DEL_URL.app_version.api}${id}/`, {}, props)
            if (!isOk(res)) failedIds.push(id)
          }
          const deletedCount = selectedIds.length - failedIds.length
          if (failedIds.length) {
            Swal.fire({
              icon: deletedCount ? 'warning' : 'error',
              title: deletedCount ? 'Partially deleted' : 'Delete Failed',
              text: `${deletedCount} removed, ${failedIds.length} could not be deleted (ID: ${failedIds.join(', ')}).`,
            })
          } else {
            Swal.fire({ icon: 'success', title: 'Deleted', text: `${deletedCount} version(s) removed.`, timer: 1500, showConfirmButton: false })
          }
          setSelectedIds(failedIds)
          loadRows()
          fetchPackages()
        } catch (err) {
          Swal.fire({ icon: 'error', title: 'Delete Failed', text: err?.response?.data?.Reason || 'An error occurred.' })
        } finally {
          setBulkSubmitting(false)
        }
      })
    }
  }

  const handleBulkVersionSubmit = async () => {
    if (!bulkVersionName.trim()) {
      Swal.fire({ icon: 'warning', title: 'Version Required', text: 'Please enter a version number.' })
      return
    }
    if (!/^\d+(\.\d+)*$/.test(bulkVersionName.trim())) {
      Swal.fire({ icon: 'warning', title: 'Invalid Version', text: 'Version should follow semantic format (e.g. 1.0.0)' })
      return
    }
    try {
      setBulkSubmitting(true)
      const res = await postRequest(`${POST_URL.app_version.api}bulk-update/`, { ids: selectedIds, version_name: bulkVersionName.trim() }, props)
      if (!isOk(res)) return
      Swal.fire({ icon: 'success', title: 'Updated', text: `Version updated to ${bulkVersionName.trim()} for ${selectedIds.length} record(s).`, timer: 1500, showConfirmButton: false })
      setBulkDialogOpen(false)
      setSelectedIds([])
      loadRows()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Bulk Update Failed', text: err?.response?.data?.Reason || 'An error occurred.' })
    } finally {
      setBulkSubmitting(false)
    }
  }

  // Inline edit handlers
  const handleInlineEditStart = (row) => {
    setInlineEditId(row.id)
    setInlineEditValue(row.version_name)
  }

  const handleInlineEditCancel = () => {
    setInlineEditId(null)
    setInlineEditValue('')
  }

  const handleInlineEditSave = async (row) => {
    const newVersion = inlineEditValue.trim()
    if (!newVersion) {
      Swal.fire({ icon: 'warning', title: 'Version Required', text: 'Version cannot be empty.' })
      return
    }
    if (!/^\d+(\.\d+)*$/.test(newVersion)) {
      Swal.fire({ icon: 'warning', title: 'Invalid Version', text: 'Use semantic format (e.g. 1.0.0)' })
      return
    }
    if (newVersion === row.version_name) {
      handleInlineEditCancel()
      return
    }
    try {
      setInlineSaving(true)
      const res = await putRequest(`${PUT_URL.app_version.api}${row.id}/`, {
        app_host: row.app_host,
        version_name: newVersion,
        app_type: row.app_type,
        device_type: row.device_type,
        is_current_version: row.is_current_version,
        need_force_update: row.need_force_update,
      }, props)
      if (!isOk(res)) return
      handleInlineEditCancel()
      loadRows()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Update Failed', text: err?.response?.data?.Reason || 'Could not update version.' })
    } finally {
      setInlineSaving(false)
    }
  }

  // DataGrid Columns definition
  const columns = [
    {
      field: 'app_host',
      headerName: 'Package Name (App Host)',
      flex: 1.4,
      minWidth: 240,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: 'primary.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              fontWeight: 700,
            }}
          >
            <Apps sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary', lineHeight: 1.2 }}>
              {params.row.app_host}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
              ID: #{params.row.id}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'version_name',
      headerName: 'Version',
      width: 170,
      renderCell: (params) => {
        const isEditing = inlineEditId === params.row.id
        if (isEditing) {
          return (
            <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
              <TextField
                size="small"
                value={inlineEditValue}
                onChange={(e) => setInlineEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleInlineEditSave(params.row)
                  if (e.key === 'Escape') handleInlineEditCancel()
                }}
                autoFocus
                disabled={inlineSaving}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 30,
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: '6px',
                    bgcolor: '#fff',
                    '& fieldset': { borderColor: '#4680FF' },
                  },
                  '& .MuiOutlinedInput-input': { px: 1, py: 0.5 },
                  width: 90,
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleInlineEditSave(params.row)}
                disabled={inlineSaving}
                sx={{ color: '#10B981', p: 0.3, '&:hover': { bgcolor: '#F0FDF4' } }}
              >
                <Check sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleInlineEditCancel}
                disabled={inlineSaving}
                sx={{ color: '#94A3B8', p: 0.3, '&:hover': { bgcolor: '#FEF2F2', color: '#EF4444' } }}
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )
        }
        return (
          <Tooltip title="Click to edit version" arrow placement="top">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover .edit-hint': { opacity: 1 },
              }}
              onClick={(e) => { e.stopPropagation(); handleInlineEditStart(params.row) }}
            >
              <Chip
                label={`v${params.value}`}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: params.row.is_current_version ? '#DCFCE7' : '#F1F5F9',
                  color: params.row.is_current_version ? '#15803D' : '#475569',
                  border: '1px solid',
                  borderColor: params.row.is_current_version ? '#86EFAC' : '#CBD5E1',
                  cursor: 'pointer',
                }}
              />
              <EditOutlined
                className="edit-hint"
                sx={{ fontSize: 14, color: '#94A3B8', opacity: 0, transition: 'opacity 0.15s' }}
              />
            </Box>
          </Tooltip>
        )
      },
    },
    {
      field: 'app_type',
      headerName: 'App Type',
      width: 140,
      renderCell: (params) => {
        const isStaff = params.value === 'staff'
        return (
          <Chip
            icon={isStaff ? <BadgeOutlined sx={{ fontSize: '15px !important' }} /> : <School sx={{ fontSize: '15px !important' }} />}
            label={isStaff ? 'Staff' : 'Student'}
            size="small"
            sx={{
              fontWeight: 600,
              bgcolor: isStaff ? '#FEF3C7' : '#EDE9FE',
              color: isStaff ? '#B45309' : '#6D28D9',
              textTransform: 'capitalize',
            }}
          />
        )
      },
    },
    {
      field: 'device_type',
      headerName: 'Platform',
      width: 140,
      renderCell: (params) => {
        const isAndroid = params.value === 'android'
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            {isAndroid ? (
              <Android sx={{ color: '#22C55E', fontSize: 20 }} />
            ) : (
              <Apple sx={{ color: '#0F172A', fontSize: 20 }} />
            )}
            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
              {isAndroid ? 'Android' : 'iOS'}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: 'is_current_version',
      headerName: 'Active Status',
      width: 160,
      renderCell: (params) => {
        const isCurrent = Boolean(params.value)
        return isCurrent ? (
          <Tooltip title="This version is served as the active current version to mobile users">
            <Chip
              icon={<CheckCircle sx={{ fontSize: '16px !important' }} />}
              label="Current Active"
              size="small"
              color="success"
              sx={{ fontWeight: 600 }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Click 'Make Current' under actions to set this as the active release">
            <Chip
              label="Older Version"
              size="small"
              variant="outlined"
              sx={{ color: 'text.secondary', borderColor: '#E2E8F0', fontWeight: 500 }}
            />
          </Tooltip>
        )
      },
    },
    {
      field: 'need_force_update',
      headerName: 'Force Update',
      width: 170,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch
            size="small"
            checked={Boolean(params.value)}
            onChange={() => handleToggleForceUpdate(params.row)}
            color="error"
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: params.value ? 'error.main' : 'text.secondary',
            }}
          >
            {params.value ? 'Required' : 'Optional'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 190,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {!params.row.is_current_version && (
            <Tooltip title="Make this version the Current Active Version">
              <IconButton
                size="small"
                onClick={() => handleSetCurrent(params.row)}
                sx={{
                  color: 'success.main',
                  bgcolor: '#F0FDF4',
                  '&:hover': { bgcolor: '#DCFCE7' },
                }}
              >
                <CheckCircle sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Edit version details">
            <IconButton
              size="small"
              onClick={() => handleOpenEdit(params.row)}
              sx={{
                color: 'primary.main',
                bgcolor: '#EFF6FF',
                '&:hover': { bgcolor: '#DBEAFE' },
              }}
            >
              <Edit sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete version">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row)}
              sx={{
                color: 'error.main',
                bgcolor: '#FEF2F2',
                '&:hover': { bgcolor: '#FEE2E2' },
              }}
            >
              <Delete sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  // Grouped packages for Card View
  const packagesGrouped = React.useMemo(() => {
    const map = {}
    rows.forEach((r) => {
      const pkg = r.app_host || 'unknown'
      if (!map[pkg]) {
        map[pkg] = {
          name: pkg,
          versions: [],
          currentAndroidStudent: null,
          currentAndroidStaff: null,
          currentIosStudent: null,
          currentIosStaff: null,
        }
      }
      map[pkg].versions.push(r)
      if (r.is_current_version) {
        if (r.device_type === 'android' && r.app_type === 'student') map[pkg].currentAndroidStudent = r
        if (r.device_type === 'android' && r.app_type === 'staff') map[pkg].currentAndroidStaff = r
        if (r.device_type === 'ios' && r.app_type === 'student') map[pkg].currentIosStudent = r
        if (r.device_type === 'ios' && r.app_type === 'staff') map[pkg].currentIosStaff = r
      }
    })
    return Object.values(map)
  }, [rows])

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Page Title & Main Actions */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #E2E8F0',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: '#4680FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  boxShadow: '0 4px 12px rgba(70, 128, 255, 0.35)',
                }}
              >
                <Apps sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#0F172A', letterSpacing: '-0.02em' }}>
                  App Versions & Packages
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748B', mt: 0.3 }}>
                  Manage mobile application versions, release updates, configure force updates, and register new school packages.
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                fetchPackages()
                loadRows()
              }}
              sx={{
                borderRadius: '8px',
                borderColor: '#CBD5E1',
                color: '#475569',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { borderColor: '#94A3B8', bgcolor: '#F1F5F9' },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineOutlined />}
              onClick={handleOpenAddPackage}
              sx={{
                borderRadius: '8px',
                bgcolor: '#4680FF',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 6px rgba(70, 128, 255, 0.4)',
                '&:hover': { bgcolor: '#2563EB' },
              }}
            >
              + Add New Package
            </Button>
          </Grid>
        </Grid>

        {/* Stats Metrics Row */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card elevation={0} sx={{ bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 2 }}>
              <CardContent sx={{ p: '14px !important' }}>
                <Typography variant="caption" sx={{ color: '#1E40AF', fontWeight: 600, textTransform: 'uppercase' }}>
                  Total Packages
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#1E3A8A', mt: 0.5 }}>
                  {metrics.totalPackages}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card elevation={0} sx={{ bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2 }}>
              <CardContent sx={{ p: '14px !important' }}>
                <Typography variant="caption" sx={{ color: '#166534', fontWeight: 600, textTransform: 'uppercase' }}>
                  Total Releases
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#14532D', mt: 0.5 }}>
                  {metrics.totalReleases}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card elevation={0} sx={{ bgcolor: '#FAF5FF', border: '1px solid #E9D5FF', borderRadius: 2 }}>
              <CardContent sx={{ p: '14px !important' }}>
                <Typography variant="caption" sx={{ color: '#6B21A8', fontWeight: 600, textTransform: 'uppercase' }}>
                  Active Current
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#581C87', mt: 0.5 }}>
                  {metrics.currentActive}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card elevation={0} sx={{ bgcolor: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 2 }}>
              <CardContent sx={{ p: '14px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Android sx={{ fontSize: 16, color: '#16A34A' }} />
                  <Typography variant="caption" sx={{ color: '#166534', fontWeight: 600, textTransform: 'uppercase' }}>
                    Android Active
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#14532D', mt: 0.5 }}>
                  {metrics.androidCurrent}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card elevation={0} sx={{ bgcolor: '#F8FAFC', border: '1px solid #CBD5E1', borderRadius: 2 }}>
              <CardContent sx={{ p: '14px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Apple sx={{ fontSize: 16, color: '#0F172A' }} />
                  <Typography variant="caption" sx={{ color: '#334155', fontWeight: 600, textTransform: 'uppercase' }}>
                    iOS Active
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#0F172A', mt: 0.5 }}>
                  {metrics.iosCurrent}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card elevation={0} sx={{ bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 2 }}>
              <CardContent sx={{ p: '14px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningAmber sx={{ fontSize: 16, color: '#DC2626' }} />
                  <Typography variant="caption" sx={{ color: '#991B1B', fontWeight: 600, textTransform: 'uppercase' }}>
                    Force Updates
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#7F1D1D', mt: 0.5 }}>
                  {metrics.forceUpdates}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Filter and Search Bar */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search package name or version..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#94A3B8', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <Autocomplete
              size="small"
              options={existingPackages}
              value={selectedPackage || null}
              onChange={(event, newValue) => {
                setSelectedPackage(newValue || '')
                setPage(0)
              }}
              renderInput={(params) => <TextField {...params} placeholder="All Packages" label="Package filter" />}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>App Type</InputLabel>
              <Select
                value={filterAppType}
                label="App Type"
                onChange={(e) => {
                  setFilterAppType(e.target.value)
                  setPage(0)
                }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="student">Student App</MenuItem>
                <MenuItem value="staff">Staff App</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Platform</InputLabel>
              <Select
                value={filterDeviceType}
                label="Platform"
                onChange={(e) => {
                  setFilterDeviceType(e.target.value)
                  setPage(0)
                }}
              >
                <MenuItem value="all">All Platforms</MenuItem>
                <MenuItem value="android">Android</MenuItem>
                <MenuItem value="ios">iOS</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterCurrentOnly}
                  onChange={(e) => {
                    setFilterCurrentOnly(e.target.checked)
                    setPage(0)
                  }}
                />
              }
              label={<Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>Current Only</Typography>}
            />

            {(searchTerm || selectedPackage || filterAppType !== 'all' || filterDeviceType !== 'all' || filterCurrentOnly) && (
              <Tooltip title="Clear all filters">
                <Button size="small" color="inherit" onClick={handleClearFilters} sx={{ textTransform: 'none', minWidth: 'auto' }}>
                  Reset
                </Button>
              </Tooltip>
            )}

            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(e, next) => next && setViewMode(next)}
              sx={{ bgcolor: '#F1F5F9' }}
            >
              <ToggleButton value="table">
                <ViewList sx={{ fontSize: 18 }} />
              </ToggleButton>
              <ToggleButton value="cards">
                <ViewModule sx={{ fontSize: 18 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Action Toolbar */}
      <Fade in={selectedIds.length > 0}>
        <Paper
          elevation={6}
          sx={{
            position: 'sticky',
            top: 8,
            zIndex: 10,
            mb: 2,
            p: 1.5,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
            display: selectedIds.length > 0 ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Badge badgeContent={selectedIds.length} color="primary" max={999}>
              <SelectAll sx={{ color: '#fff', fontSize: 22 }} />
            </Badge>
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
              {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected
            </Typography>
            <Button
              size="small"
              onClick={() => setSelectedIds([])}
              startIcon={<Deselect sx={{ fontSize: '16px !important' }} />}
              sx={{ color: '#94A3B8', textTransform: 'none', fontWeight: 500, fontSize: 12, '&:hover': { color: '#fff' } }}
            >
              Clear
            </Button>
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<SystemUpdateAlt sx={{ fontSize: '16px !important' }} />}
              onClick={() => handleBulkAction('update_version')}
              disabled={bulkSubmitting}
              sx={{
                bgcolor: '#4680FF',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                fontSize: 12,
                '&:hover': { bgcolor: '#2563EB' },
              }}
            >
              Update Version
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<WarningAmber sx={{ fontSize: '16px !important' }} />}
              onClick={() => handleBulkAction('force_update_on')}
              disabled={bulkSubmitting}
              sx={{
                bgcolor: '#F59E0B',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                fontSize: 12,
                '&:hover': { bgcolor: '#D97706' },
              }}
            >
              Force Update ON
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleBulkAction('force_update_off')}
              disabled={bulkSubmitting}
              sx={{
                borderColor: '#64748B',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                fontSize: 12,
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Force Update OFF
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<DeleteSweep sx={{ fontSize: '16px !important' }} />}
              onClick={() => handleBulkAction('delete')}
              disabled={bulkSubmitting}
              sx={{
                bgcolor: '#EF4444',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                fontSize: 12,
                '&:hover': { bgcolor: '#DC2626' },
              }}
            >
              Delete Selected
            </Button>
          </Stack>
        </Paper>
      </Fade>

      {/* Main Content Area */}
      {viewMode === 'table' ? (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              rowCount={totalCount}
              loading={loading}
              pageSize={pageSize}
              page={page}
              pagination
              paginationMode="server"
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize)
                setPage(0)
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              checkboxSelection
              onSelectionModelChange={(newSelection) => setSelectedIds(newSelection)}
              selectionModel={selectedIds}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: '#F8FAFC',
                  borderBottom: '1px solid #E2E8F0',
                  fontWeight: 700,
                  color: '#334155',
                },
                '& .MuiDataGrid-row': {
                  borderBottom: '1px solid #F1F5F9',
                  '&:hover': {
                    bgcolor: '#F8FAFC',
                  },
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  bgcolor: '#EFF6FF',
                  '&:hover': { bgcolor: '#DBEAFE' },
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </Box>
        </Paper>
      ) : (
        /* Package Cards View */
        <Grid container spacing={2.5}>
          {packagesGrouped.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                <Typography variant="h6" color="text.secondary">
                  No packages matching the selected filters
                </Typography>
              </Paper>
            </Grid>
          ) : (
            packagesGrouped.map((pkg) => (
              <Grid item xs={12} sm={6} md={4} key={pkg.name}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid #E2E8F0',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.06)',
                      borderColor: '#CBD5E1',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '10px',
                            bgcolor: '#EEF2FF',
                            color: '#4F46E5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Apps sx={{ fontSize: 22 }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0F172A', wordBreak: 'break-all', lineHeight: 1.2 }}>
                            {pkg.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748B' }}>
                            {pkg.versions.length} release{pkg.versions.length !== 1 ? 's' : ''} recorded
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Active Versions Matrix */}
                    <Typography variant="caption" fontWeight={600} sx={{ color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Current Active Releases
                    </Typography>

                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {/* Android */}
                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          bgcolor: '#F0FDF4',
                          border: '1px solid #DCFCE7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Android sx={{ color: '#16A34A', fontSize: 18 }} />
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#166534' }}>
                            Android
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.8 }}>
                          {pkg.currentAndroidStudent && (
                            <Chip
                              size="small"
                              label={`Student: v${pkg.currentAndroidStudent.version_name}`}
                              sx={{ bgcolor: '#DCFCE7', color: '#15803D', fontWeight: 600, fontSize: 11 }}
                            />
                          )}
                          {pkg.currentAndroidStaff && (
                            <Chip
                              size="small"
                              label={`Staff: v${pkg.currentAndroidStaff.version_name}`}
                              sx={{ bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 600, fontSize: 11 }}
                            />
                          )}
                          {!pkg.currentAndroidStudent && !pkg.currentAndroidStaff && (
                            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                              None active
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* iOS */}
                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          bgcolor: '#F8FAFC',
                          border: '1px solid #E2E8F0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Apple sx={{ color: '#0F172A', fontSize: 18 }} />
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A' }}>
                            iOS
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.8 }}>
                          {pkg.currentIosStudent && (
                            <Chip
                              size="small"
                              label={`Student: v${pkg.currentIosStudent.version_name}`}
                              sx={{ bgcolor: '#E0E7FF', color: '#3730A3', fontWeight: 600, fontSize: 11 }}
                            />
                          )}
                          {pkg.currentIosStaff && (
                            <Chip
                              size="small"
                              label={`Staff: v${pkg.currentIosStaff.version_name}`}
                              sx={{ bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 600, fontSize: 11 }}
                            />
                          )}
                          {!pkg.currentIosStudent && !pkg.currentIosStaff && (
                            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                              None active
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Create / Edit Modal Dialog */}
      <Dialog
        open={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
          {modalMode === 'new_package'
            ? 'Add New App Package'
            : modalMode === 'edit_version'
            ? 'Edit App Version'
            : 'Release New App Version'}
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400, mt: 0.5 }}>
            {modalMode === 'new_package'
              ? 'Register a new application package host identifier and configure initial version release.'
              : 'Deploy a new version release or update existing mobile deployment.'}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: '#F1F5F9' }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Package / App Host field */}
            {modalMode === 'new_package' ? (
              <TextField
                fullWidth
                label="Package Name / App Host *"
                placeholder="e.g. com.myschool.student or com.myschool.staff"
                value={form.app_host}
                onChange={(e) => setForm({ ...form, app_host: e.target.value })}
                error={Boolean(formErrors.app_host)}
                helperText={formErrors.app_host || 'Unique Android package name / iOS bundle identifier'}
              />
            ) : (
              <Autocomplete
                freeSolo
                options={existingPackages}
                value={form.app_host}
                onInputChange={(event, newInputValue) => {
                  setForm({ ...form, app_host: newInputValue })
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Package Name / App Host *"
                    placeholder="Select existing or type custom package"
                    error={Boolean(formErrors.app_host)}
                    helperText={formErrors.app_host || 'Select existing package or type a new one'}
                  />
                )}
              />
            )}

            {/* Version Name */}
            <TextField
              fullWidth
              label="Version Number *"
              placeholder="e.g. 1.0.49 or 2.1.0"
              value={form.version_name}
              onChange={(e) => setForm({ ...form, version_name: e.target.value })}
              error={Boolean(formErrors.version_name)}
              helperText={formErrors.version_name || 'Semantic version string compared by mobile apps during update check'}
            />

            {/* App Type & Device Type */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>App Type *</InputLabel>
                  <Select
                    value={form.app_type}
                    label="App Type *"
                    onChange={(e) => setForm({ ...form, app_type: e.target.value })}
                  >
                    {APP_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={createBothPlatforms}>
                  <InputLabel>Platform *</InputLabel>
                  <Select
                    value={form.device_type}
                    label="Platform *"
                    onChange={(e) => setForm({ ...form, device_type: e.target.value })}
                  >
                    {DEVICE_TYPES.map((d) => (
                      <MenuItem key={d.value} value={d.value}>
                        {d.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {modalMode === 'new_package' && (
              <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px dashed #CBD5E1' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={createBothPlatforms}
                      onChange={(e) => setCreateBothPlatforms(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Deploy for both Android and iOS simultaneously
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Creates identical initial version records for both platforms automatically.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}

            {/* Switches: Is Current & Force Update */}
            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
              <Stack spacing={1.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_current_version}
                      onChange={(e) => setForm({ ...form, is_current_version: e.target.checked })}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Set as Current Active Version
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        This version will immediately become the active target for clients checking for updates.
                      </Typography>
                    </Box>
                  }
                />

                <Divider />

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.need_force_update}
                      onChange={(e) => setForm({ ...form, need_force_update: e.target.checked })}
                      color="error"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Require Force Update
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Users on older versions will be blocked until they update the app to this version.
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setModalOpen(false)}
            disabled={submitting}
            sx={{ textTransform: 'none', color: '#64748B', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFormSubmit}
            disabled={submitting}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#4680FF',
              px: 3,
              '&:hover': { bgcolor: '#2563EB' },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : modalMode === 'edit_version' ? (
              'Save Changes'
            ) : (
              'Save & Release'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Version Update Dialog */}
      <Dialog
        open={bulkDialogOpen}
        onClose={() => !bulkSubmitting && setBulkDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
          Update Version for {selectedIds.length} Item{selectedIds.length !== 1 ? 's' : ''}
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400, mt: 0.5 }}>
            Enter the new version number to apply to all selected app version records.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#F1F5F9' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="New Version Number *"
              placeholder="e.g. 1.0.49 or 2.1.0"
              value={bulkVersionName}
              onChange={(e) => setBulkVersionName(e.target.value)}
              helperText="This version will be applied to all selected records"
              autoFocus
            />
            <Box sx={{ p: 1.5, bgcolor: '#FEF3C7', borderRadius: 2, border: '1px solid #FDE68A' }}>
              <Typography variant="caption" sx={{ color: '#92400E', fontWeight: 600 }}>
                ⚠️ This will update the version for {selectedIds.length} record{selectedIds.length !== 1 ? 's' : ''} simultaneously.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setBulkDialogOpen(false)}
            disabled={bulkSubmitting}
            sx={{ textTransform: 'none', color: '#64748B', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkVersionSubmit}
            disabled={bulkSubmitting}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#4680FF',
              px: 3,
              '&:hover': { bgcolor: '#2563EB' },
            }}
          >
            {bulkSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Update All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AppVersionList
